import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase, directFetch } from '@/lib/supabase';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import StorageImage from '@/components/base/StorageImage';

interface BlogPostRow {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  cover_image: string;
  author: string;
  published_at: string;
  tags: string[] | null;
}

const POSTS_PER_PAGE = 6;

export default function Blog() {
  const [posts, setPosts] = useState<BlogPostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [page, setPage] = useState(1);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Primary: direct REST fetch (works around backend gateway issues with the publishable key)
      const { data: directData, error: directError } = await directFetch('blog_posts', {
        select: 'id,title,slug,excerpt,cover_image,author,published_at,tags',
        status: 'eq.published',
        order: 'published_at.desc',
      });

      if (!directError && directData) {
        setPosts((directData as BlogPostRow[]) || []);
        return;
      }

      // Fallback: supabase-js client
      const { data, error: fetchError } = await supabase
        .from('blog_posts')
        .select('id, title, slug, excerpt, cover_image, author, published_at, tags')
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      if (fetchError) {
        const errAny = fetchError as any;
        const msg = errAny.message || errAny.Message || errAny.details || errAny.Details || '';
        setError(`${msg || 'Could not retrieve blog posts'}${directError ? ` (direct fetch: ${directError})` : ''}`);
        return;
      }
      setPosts((data as BlogPostRow[]) || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg ? `Unexpected error: ${msg}` : 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    posts.forEach((p) => (p.tags || []).forEach((l) => cats.add(l)));
    return ['All', ...Array.from(cats).sort()];
  }, [posts]);

  const filtered = useMemo(() => {
    let result = posts;
    if (activeCategory !== 'All') {
      result = result.filter((p) =>
        (p.tags || []).some((l) => l.toLowerCase() === activeCategory.toLowerCase())
      );
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          (p.excerpt || '').toLowerCase().includes(q) ||
          (p.tags || []).some((l) => l.toLowerCase().includes(q))
      );
    }
    return result;
  }, [activeCategory, search, posts]);

  const totalPages = Math.ceil(filtered.length / POSTS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * POSTS_PER_PAGE, page * POSTS_PER_PAGE);
  const featured = filtered[0];

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="py-14 md:py-20 bg-background-100/70">
        <div className="max-w-[800px] mx-auto px-4 md:px-6 lg:px-10 text-center">
          <p className="text-primary-500 font-body text-sm tracking-widest uppercase mb-3">Blog</p>
          <h2 className="font-heading text-foreground-900 text-3xl md:text-4xl lg:text-5xl mb-4">
            Real Estate Advice for Homeowners
          </h2>
          <p className="text-foreground-600 font-body text-base max-w-[550px] mx-auto mb-8">
            Practical guidance on selling, home valuation, market trends and more — from Carol L Berry.
          </p>
          <div className="max-w-[450px] mx-auto">
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search articles..."
                className="w-full border border-background-300 rounded-full px-5 py-2.5 pl-11 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent bg-background-50"
              />
              <i className="ri-search-line text-foreground-400 absolute left-4 top-1/2 -translate-y-1/2 text-base" />
            </div>
          </div>
        </div>
      </section>

      {/* Categories + Edit Blog */}
      <section className="py-6 bg-background-50 border-b border-background-200/70">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setActiveCategory(cat); setPage(1); }}
                  className={`px-4 py-1.5 rounded-full text-xs font-body font-medium whitespace-nowrap transition-all ${
                    activeCategory === cat
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <Link
              to="/admin/login"
              className="inline-flex items-center gap-1.5 text-foreground-500 hover:text-foreground-700 font-body text-xs transition-colors whitespace-nowrap px-3 py-1.5 rounded-full bg-background-100 hover:bg-background-200"
              title="Edit Blog"
            >
              <i className="ri-edit-line text-sm" />
              Edit Blog
            </Link>
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="py-14 md:py-20 bg-background-50 flex-1">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <i className="ri-error-warning-line text-foreground-300 text-5xl mb-4 block" />
              <p className="text-foreground-500 font-body text-base mb-4">{error}</p>
              <button
                onClick={() => fetchPosts()}
                className="px-4 py-2 rounded-md text-sm font-body font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16">
              <i className="ri-file-search-line text-foreground-300 text-5xl mb-4 block" />
              <p className="text-foreground-500 font-body text-base">No articles found matching your search.</p>
            </div>
          ) : (
            <>
              {/* Featured */}
              {featured && activeCategory === 'All' && !search.trim() && (
                <div className="mb-12">
                  <p className="text-primary-500 font-body text-xs tracking-widest uppercase mb-4">Featured Article</p>
                  <Link to={`/blog/${featured.slug}`} className="group flex flex-col md:flex-row gap-6 bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200/60 hover:-translate-y-1 transition-all duration-300">
                    <div className="w-full md:w-[420px] flex-shrink-0">
                      <div className="aspect-[16/10] md:aspect-auto md:h-full overflow-hidden">
                        <StorageImage
                          path={featured.cover_image}
                          alt={featured.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[400ms]"
                        />
                      </div>
                    </div>
                    <div className="flex-1 p-5 md:p-7 flex flex-col justify-center">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {(featured.tags || []).slice(0, 2).map((l) => (
                          <span key={l} className="bg-primary-50 text-primary-600 font-body text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full group-hover:bg-primary-100 transition-colors duration-300">{l}</span>
                        ))}
                      </div>
                      <h3 className="font-heading text-foreground-900 text-xl md:text-2xl mb-3 group-hover:text-primary-600 transition-colors duration-300">{featured.title}</h3>
                      <p className="text-foreground-600 font-body text-sm leading-relaxed mb-4 group-hover:text-foreground-700 transition-colors duration-300">{featured.excerpt}</p>
                      <div className="flex items-center gap-4 text-foreground-400 font-body text-xs group-hover:text-foreground-500 transition-colors duration-300">
                        <span>{formatDate(featured.published_at)}</span>
                        <span>·</span>
                        <span>{featured.author}</span>
                      </div>
                    </div>
                  </Link>
                </div>
              )}

              {/* Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginated.map((post) => (
                  <Link key={post.id} to={`/blog/${post.slug}`} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200/60 hover:-translate-y-1 transition-all duration-300">
                    <div className="aspect-[16/10] overflow-hidden">
                      <StorageImage
                        path={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[400ms]"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {(post.tags || []).slice(0, 2).map((l) => (
                          <span key={l} className="bg-primary-50 text-primary-600 font-body text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full group-hover:bg-primary-100 transition-colors duration-300">{l}</span>
                        ))}
                      </div>
                      <h3 className="font-heading text-foreground-900 text-base mb-2 group-hover:text-primary-600 transition-colors duration-300 line-clamp-2">{post.title}</h3>
                      <p className="text-foreground-500 font-body text-xs line-clamp-2 mb-3 group-hover:text-foreground-600 transition-colors duration-300">{post.excerpt}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-foreground-400 font-body text-xs group-hover:text-foreground-500 transition-colors duration-300">{formatDate(post.published_at)}</span>
                        <span className="text-primary-500 font-body text-xs font-medium flex items-center gap-1">Read More <i className="ri-arrow-right-line text-xs group-hover:translate-x-1 transition-transform duration-300" /></span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-12">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-body transition-colors ${
                        page === i + 1
                          ? 'bg-primary-500 text-background-50'
                          : 'border border-background-300 text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-foreground-950">
        <div className="max-w-[700px] mx-auto px-4 md:px-6 lg:px-10 text-center">
          <h2 className="font-heading text-accent-100 text-2xl md:text-3xl mb-4">
            Thinking About Selling Your Home?
          </h2>
          <p className="text-background-100/70 font-body text-base mb-8">
            Get clear, personal guidance on your property's potential value and the next steps involved.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/home-valuation" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-md text-base font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-all">
              <i className="ri-home-4-line text-lg" />Request a Free Home Valuation
            </Link>
            <Link to="/contact" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-accent-300 text-accent-100 px-8 py-3.5 rounded-md text-base font-body font-semibold whitespace-nowrap hover:bg-accent-300/10 transition-all">
              <i className="ri-chat-3-line text-lg" />Speak With Carol
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}